class OfflineExporter {
  static typeConversions = [
    {
      class: Set,
      string: "$THIS_IS_A_SET$",
      serialize: (set) => [...set],
      parse: (array) => new Set(array),
    },
    {
      class: Map,
      string: "$THIS_IS_A_MAP$",
      serialize: (map) => [...map],
      parse: (array) => new Map(array),
    },
  ];
  static fileId = "$THIS_IS_A_FILE$";

  async export({ onlyIncludeObjectStores = null } = {}) {
    const resourcesByKey = ResourceController.getAll();
    const data = {};

    for (const key in resourcesByKey) {
      const resource = resourcesByKey[key];
      data[key] = {};

      await this._forEachDataStore(resource, onlyIncludeObjectStores, async function (store, name) {
        data[key][name] = await OfflineExporter._getStoreData(store, name);
      });
    }

    const json = OfflineExporter._stringify(data);

    return json;
  }

  async forEachDatum(callback, { onlyIncludeObjectStores = null } = {}) {
    const resourcesByKey = ResourceController.getAll();

    for (const resourceKey in resourcesByKey) {
      const resource = resourcesByKey[resourceKey];

      await this._forEachDataStore(resource, onlyIncludeObjectStores, async function (store) {
        await OfflineExporter._forEachStoreDatum(store, callback, resourceKey);
      });
    }
  }

  async _forEachDataStore(resource, onlyIncludeObjectStores, callback) {
    const stores = resource._getCachedObjectStores();

    for (const name in stores) {
      if (onlyIncludeObjectStores && !onlyIncludeObjectStores.includes(name)) {
        continue;
      }

      await callback(stores[name], name);
    }
  }

  static async _getStoreData(store, name) {
    const data = await store._dbGetAll();

    if (name === "cachedFileStore") {
      await OfflineExporter._serializeFiles(data);
    }

    return data;
  }

  static async _forEachStoreDatum(store, callback, resourceKey) {
    let transaction = await store._getDbObjectStoreTransaction();
    const allKeys = await transaction.getAllKeys();

    for (const key of allKeys) {
      const datum = await transaction.get(key);
      await callback(datum, resourceKey);
      transaction = await store._getDbObjectStoreTransaction();
    }
  }

  static async _serializeFiles(data) {
    for (const datum of data) {
      if (!datum?.file) {
        continue;
      }

      const type = datum.file.type;
      const typeLength = type.length.toString().padStart(3, "0");
      datum.file =
        OfflineExporter.fileId +
        typeLength +
        type +
        (await OfflineExporter._blobAsBase64(datum.file));
    }
  }

  static async _blobAsBase64(blob) {
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    // Remove prefixed "data:*/*;base64,"
    const base64Index = base64.indexOf("base64,") + 7;
    return base64.slice(base64Index);
  }

  static _stringify(data) {
    const dataTypes = new Set();

    data = JSON.stringify(data, (key, value) => OfflineExporter._replacer(key, value, dataTypes));
    console.log("Data types found:", dataTypes);

    return data;
  }

  static _replacer(key, value, dataTypes) {
    dataTypes.add(value?.constructor?.name);

    for (const { class: Class, string, serialize } of OfflineExporter.typeConversions) {
      if (value instanceof Class) {
        return [string, serialize(value)];
      }
    }

    return value;
  }

  static async exportAndDownloadOfflineData(options = {}) {
    LoadingScreen.show();

    const exporter = new OfflineExporter();
    const json = await exporter.export(options);
    const blob = new Blob([json], {
      type: "application/json",
    });
    Downloader.fromBlob(blob);

    LoadingScreen.hide();
  }

  static async uploadOfflineData() {
    try {
      await MessageModal.showConfirmWarningModalAsPromise(
        `
        <p>Before uploading a debug report, please <a href='https://kb.2ndnaturewater.com/knowledge/kb-tickets/new' target='_blank'>contact support</a> for assistance.</p>
        <p>This will upload local browser data to our servers for debugging. Please make sure you have a strong WiFi signal before proceeding, and be aware the upload may take several minutes. Leave the browser open and your device awake while the upload happens.</p>
      `,
        { okMessage: "Upload", returnMessage: "Cancel" },
      );
    } catch (e) {
      return;
    }

    LoadingScreen.show();
    const id = new Date().getTime();

    try {
      const exporter = new OfflineExporter();
      await OfflineExporter._uploadMetadata(exporter, id);
      await OfflineExporter._uploadFiles(exporter, id);
      await OfflineExporter._runAndUploadDebugTests(id);
    } catch (e) {
      LoadingScreen.hide();
      Sentry.captureException(e);
      MessageModal.showSimpleWarningModal(
        "An error occurred while uploading the debug report, please try again later or <a href='https://kb.2ndnaturewater.com/knowledge/kb-tickets/new' target='_blank'>contact support</a> for assistance.",
      );
      return;
    }

    LoadingScreen.hide();
    const uploadPrefix = `${FormSettings.getDirectUploadPost().fileNameValuePrefix}${id}`;
    // Sentry groups synthetic exceptions by message text, and its tokenizer replaces digit runs
    // with placeholders, so every upload would otherwise collapse into one issue.
    Sentry.captureException(
      `User uploaded debug report to "${uploadPrefix}" (there may be multiple files with this prefix)\nRun "await $2NLib.commands.importOfflineData()" in the browser console to import offline data into the app.`,
      { fingerprint: ["debug-report-upload", uploadPrefix] },
    );
    MessageModal.showNoticeMessage(
      "Debug data successfully uploaded. Our team has been notified and will investigate the issue.",
    );
  }

  static async _uploadMetadata(exporter, id) {
    const json = await exporter.export({
      onlyIncludeObjectStores: ["cachedDefaultStore", "cachedMetadataStore"],
    });
    await OfflineExporter._uploadJson(json, `${id}-metadata.json`);
  }

  static async _uploadFiles(exporter, id) {
    const fileIdsByResource = {};

    await exporter.forEachDatum(
      async function (datum, resourceKey) {
        const file = new File([datum.file], `${id}-file-${datum.id}.bin`, {
          type: datum.file.type,
        });
        await DirectUpload.uploadFile(file);

        if (!fileIdsByResource[resourceKey]) {
          fileIdsByResource[resourceKey] = [];
        }
        fileIdsByResource[resourceKey].push(datum.id);
      },
      { onlyIncludeObjectStores: ["cachedFileStore"] },
    );

    await OfflineExporter._uploadJson(JSON.stringify(fileIdsByResource), `${id}-file-map.json`);
  }

  static async _runAndUploadDebugTests(id) {
    const DebugPage = require("../debugPage");
    const tests = DebugPage.runTests();
    const results = [];

    for (const testPromise of tests) {
      try {
        const result = await testPromise;
        results.push(result);
      } catch (e) {
        results.push(e);
        Sentry.captureException(e);
      }
    }

    const json = OfflineExporter._stringify(results);
    await OfflineExporter._uploadJson(json, `${id}-debug-tests.json`);
  }

  static async _uploadJson(json, filename) {
    let data = new Blob([json], { type: "application/json" });

    const compressed = await OfflineExporter._compressBlob(data);
    const isCompressed = compressed !== data;

    if (isCompressed) {
      filename = filename + ".gz";
    }

    data = new File([compressed], filename, {
      type: isCompressed ? "application/gzip" : "application/json",
    });

    await DirectUpload.uploadFile(data);
  }

  static async _compressBlob(blob) {
    if (typeof CompressionStream === "undefined") {
      return blob;
    }

    const stream = blob.stream().pipeThrough(new CompressionStream("gzip"));
    return await new Response(stream).blob();
  }
}

module.exports = OfflineExporter;

const ResourceController = require("../offline/resourceController");
const Downloader = require("../files/downloader");
const LoadingScreen = require("../general/loadingScreen");
const MessageModal = require("../modals/messageModal");
const DirectUpload = require("../files/directUpload");
const Sentry = require("@sentry/browser");
const FormSettings = require("../settings/formSettings");
